import React, { forwardRef, useEffect, useRef, useState } from "react";
import { useCart } from "modules/cart/hooks/useCart";
import { useContext } from "react";
import { AppDataContext } from "modules/main/context/appData";
import { Link, useNavigate } from "react-router-dom";
import {get_total_qty_per_prod, getTotalAmount, getTotalQty} from "utils/cartUtils";
import { toast } from "sonner";
import {ArrowLeftRight,Info, ListTodo, ListTodoIcon, RefreshCw, Search, ShoppingCart, Sparkles, Trash, WandSparkles,X} from "lucide-react";

import { Button } from "@/components/ui/button";

import { 
        Sheet, 
        SheetClose, 
        SheetContent, 
        SheetDescription, 
        SheetHeader, 
        SheetTitle, 
        SheetTrigger,
        SheetFooter } from "@/components/ui/sheet";
import { AddDeleteCartButtons } from "./cartElements";
import { ScrollArea } from "@/components/ui/scroll-area";
import Amount from "utils/amount";
import { sendViewCart } from "utils/analytics";
import { UserData } from "modules/authentication/types/user-data";
import {
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
} from "@/components/ui/empty"



import { useAuth } from "modules/authentication/hooks/use-auth";
import { LoginDialog } from "modules/authentication/components/loginDialog";
import { CartItem, Payload } from "../types/cart";
import { StoreData } from "modules/main/types/storeData";
import { Card, CardContent } from "@/components/ui/card";
import { useRequireLocationForCart } from "modules/cart/hooks/useRequireLocationForCart";
import { LocationRequiredDialog } from "modules/main/components/location-required-dialog";
import MenuIcon from "modules/main/types/menus";
import posthog from "posthog-js";
import { ProductSearchPicker } from "modules/lists/components/product-search-picker";


import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

import { AcquirePlusDialog } from "modules/subscription/components/AcquirePlusDialog";

import { ReplacementPreferences } from "./replacements";
import { Skeleton } from "@/components/ui/skeleton";
import { REPLACEMENT_ACTION_LABELS } from "modules/checkout/types/productDecission";
import { cn } from "@/lib/utils";


// import { useToast } from "@/hooks/use-toast";
// import { ChatContainer} from "modules/chat/chatContainer";

const MIN_OPTIMIZE_AMOUNT = 20000;

/** Maximum cart total (in CLP) allowed for optimization without a Plus plan. */
const MAX_OPTIMIZE_AMOUNT_WITHOUT_PLUS = 300_000;

const sendEventPH = (phEvent:"Optimizar"|"Ver Plus") => {
    posthog.capture(phEvent)
}

const getUserData = (): UserData => {
    try {
        const data = localStorage.getItem("userData");
        if (!data) return {} as UserData;
        
        const parsed = JSON.parse(data) as UserData;
        return parsed;
    } catch (error) {
        console.warn("Error parsing userData from localStorage:", error);
        return {} as UserData;
    }
};

interface CartCategoryDefinition {
    id: number;
    name: string;
    icon: string;
    orderField: number;
}

interface CartCategoryGroup {
    category: CartCategoryDefinition;
    items: CartItem[];
}

const cartCategoryDefinitions: CartCategoryDefinition[] = [
    { id: 1, name: "Ofertas", icon: "sale", orderField: 0 },
    { id: 5, name: "Despensa", icon: "store", orderField: 10 },
    { id: 6, name: "Lácteos", icon: "milk", orderField: 20 },
    { id: 9, name: "Panadera, Pasteleria", icon: "bread", orderField: 25 },
    { id: 20, name: "Quesos y Fiambres", icon: "cheese", orderField: 27 },
    { id: 10, name: "Dulces y Colaciones", icon: "candy", orderField: 28 },
    { id: 3, name: "Frutas y Verduras", icon: "fruit", orderField: 30 },
    { id: 2, name: "Carniceria", icon: "beef", orderField: 40 },
    { id: 15, name: "Congelados", icon: "frozen", orderField: 45 },
    { id: 4, name: "Bebés", icon: "baby", orderField: 50 },
    { id: 7, name: "Limpieza", icon: "clean", orderField: 60 },
    { id: 8, name: "Perfumería", icon: "persCare", orderField: 65 },
    { id: 12, name: "Botilleria", icon: "drink", orderField: 70 },
    { id: 11, name: "Mascotas", icon: "house", orderField: 80 },
];

const fallbackCartCategory: CartCategoryDefinition = {
    id: -1,
    name: "Otros",
    icon: "store",
    orderField: Number.MAX_SAFE_INTEGER,
};

/**
 * Resolves the UI metadata for a cart category id.
 */
const getCartCategoryDefinition = (categoryId: number): CartCategoryDefinition => {
    return cartCategoryDefinitions.find((category) => category.id === categoryId) ?? {
        ...fallbackCartCategory,
        id: categoryId,
    };
};

/**
 * Reads the cart category id from the available item fields.
 */
const getCartItemCategoryId = (item: CartItem): number => {
    return item.category_id;
};

/**
 * Groups cart items by category and sorts groups using the configured display order.
 */
const groupCartItemsByCategory = (cart: CartItem[]): CartCategoryGroup[] => {
    const groupedItems = new Map<number, CartCategoryGroup>();

    cart.forEach((item) => {
        const categoryId = getCartItemCategoryId(item);
        const categoryDefinition = getCartCategoryDefinition(categoryId);
        const currentGroup = groupedItems.get(categoryId);

        if (currentGroup) {
            currentGroup.items.push(item);
            return;
        }

        groupedItems.set(categoryId, {
            category: categoryDefinition,
            items: [item],
        });
    });

    return Array.from(groupedItems.values()).sort((currentGroup, nextGroup) => {
        return currentGroup.category.orderField - nextGroup.category.orderField;
    });
};

export const CartSheet = ({open, setOpen}: {open?: boolean, setOpen?: (open: boolean) => void})  => {    
    const {storeList} = useContext(AppDataContext);
    const {
        addDeletefromCart,
        removeFromCart,
        cart,
        clearCart,
        isInsertingItems,
        insertingCount,
        resolutionExecution,
        isMigrating,
        migrateCart,
    } = useCart();
    const {isLogged, isPremiumUser, requireLoginAction} = useAuth(); 
    const [isPlusDialogOpen, setIsPlusDialogOpen] = useState(false);
    const navigate = useNavigate();
    const {
        isLocationDialogOpen,
        handleLocationSelected,
        handleLocationDialogOpenChange,
    } = useRequireLocationForCart();

    // Guard for React Strict Mode — prevents duplicate migration calls
    const migrationRef = useRef(false);

    useEffect(() => {  
        const userData = getUserData();
        const cartVersion = localStorage.getItem("v") ?? "0";
        const userVersion = userData.v?.toString() ?? "";
        
        // Migration condition: user has version, cart differs from user version
        const needsMigration =
            userVersion !== "" &&
            userVersion !== cartVersion;
        
        // Only trigger migration if cart has items, not already running, and not currently migrating
        if (needsMigration && cart.length > 0 && !migrationRef.current && !isMigrating) {
            migrationRef.current = true;
            migrateCart(Number(cartVersion), cart);
        }
    }, [cart, migrateCart, isLogged]);

    const totalAmount = getTotalAmount(cart);
    const isBelowOptimizeMinimum = totalAmount < MIN_OPTIMIZE_AMOUNT;
    const isOptimizeLoading = isInsertingItems;
    /** True when the non-Plus user's cart exceeds the optimization limit. */
    const isCartLimitExceeded = !isPremiumUser && totalAmount > MAX_OPTIMIZE_AMOUNT_WITHOUT_PLUS;

    const handleOptimizeCart = () => {
        if (isBelowOptimizeMinimum) {
            toast.error("El mínimo para optimizar es de $20.000. Agrega más productos.");
            return;
        }

        sendEventPH("Optimizar");
        setOpen?.(false);
        navigate("/cart/calculate");
    };

    // v1: post-login re-click required; auto-open is a follow-up (see design Edge case 2)
    const handleUpsellClick = () => {
        if (isCartLimitExceeded && !isLogged) {
            requireLoginAction();
            return;
        }
        sendEventPH("Ver Plus");
        setIsPlusDialogOpen(true);
    };

    return (
        <Sheet {...(open!==undefined ? {open}:{ })} onOpenChange={setOpen}>
            <SheetTrigger asChild onClick={() => {sendViewCart(cart)}}>
                <Button data-ph-event="view-cart" variant="link" size="default">
                    <div className="relative inline-block">
                        <ShoppingCart  className="size-6 text-muted-foreground transition-colors hover:text-foreground"/>
                        {(cart.length>0)&&
                        <span className="px-1 absolute min-w-4 w-auto h-4 left-4 top-0 rounded-full bg-red-500 lg:bg-orange-400 text-white text-[10px]">
                            {getTotalQty(cart)}
                        </span>}
                    </div>
                </Button>
            </SheetTrigger>
            <SheetContent className="flex flex-col w-full bg-gray-50 p-0 border-0 overflow-hidden">
                <SheetHeader className="py-6 pb-0 px-6">
                    <SheetTitle>
                        <div className="flex items-center justify-between">
                            <span>Tu Carrito </span>
                            {/* <Button variant="ghost" size="sm" onClick={handleBulkUpdate} disabled={isUpdating || cart.length === 0}>
                                <ReloadIcon className={`inline-block mr-2 ${isUpdating ? "animate-spin" : ""}`}/>
                            </Button>                             */}
                        </div>                                            
                    </SheetTitle>
                    <SheetDescription>                        
                    </SheetDescription>
                </SheetHeader>
                <div className="flex flex-1 min-h-0 flex-col px-6 overflow-hidden">
                    
                    <div className="flex-1 min-h-0 overflow-hidden">
                        <CartContent 
                        cart={cart} 
                        storeList={storeList} 
                        addDeletefromCart={addDeletefromCart} 
                        removeFromCart={removeFromCart}
                        clearCart={clearCart}
                        isLogged={isLogged}
                        insertingCount={insertingCount}
                        setOpen={setOpen}
                        />
                    </div>
                </div>     
                <SheetFooter className="flex sm:flex-col  flex-col w-full sm:justify-center bg-dark pb-6 px-6 pt-3 gap-4" >
                    {/* <CharContainer 
                        // openChatDrawer={openChatDrawer} 
                        // setOpenChatDrawer={setOpenChatDrawer}
                        /> */}
                    <div className="flex flex-col w-full gap-2">
                        {
                        (cart.length > 0)&&(
                        <div className="w-full flex flex-row justify-between items-center pt-2">
                            {/* <div className="flex flex-col w-full gap-2  text-sm">
                                <div className="flex  gap-2 justify-between">
                                    <div className="font-semibold">PRODUCTOS:</div>
                                    <div className="font-bold min-w-14 text-right">{getTotalQty(cart)}</div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className=" font-semibold text-left">Precio estimado:</div>
                                    <div className="font-bold min-w-14 text-right">
                                        <Amount
                                        amount={getTotalAmount(cart)}
                                        currency="CLP" locale="es-CL"
                                        />
                                    </div>
                                </div>                                  
                            </div> */}
                            <div className="w-full flex justify-center text-base">
                            {isCartLimitExceeded ? (
                                    <div className="flex flex-col gap-2 w-full text-xs text-center">  
                                        <span>
                                            Tu carrito supera <Amount amount={MAX_OPTIMIZE_AMOUNT_WITHOUT_PLUS} currency="CLP" locale="es-CL" className="font-semibold" />, con <span className="font-semibold">Carriapp Plus</span> ahorras sin límites.
                                        </span>
                                            <Button
                                                className="mb-2 w-full font-semibold text-base"
                                                size="lg"
                                                variant="default"
                                                onClick={handleUpsellClick}
                                            >
                                                {isCartLimitExceeded && !isLogged ? "Iniciar sesión para optimizar" : "Optimizar con Plus"}
                                            </Button>
                                            <AcquirePlusDialog
                                                open={isPlusDialogOpen}
                                                onOpenChange={setIsPlusDialogOpen}
                                            />
                                    </div>
                                ) : (
                                    <Button data-ph-event="optimize-cart" className="mb-2 w-full font-semibold text-base" size="lg" 
                                        onClick={handleOptimizeCart}
                                        disabled={isOptimizeLoading}
                                    >
                                        {/* <WandSparkles className="h-5 w-5"/> */}
                                        {isOptimizeLoading ? "Actualizando carrito..." : `Optimizar ${getTotalQty(cart)} productos`}
                                    </Button>
                                )}                                                            
                            </div>                                                                            
                        </div>)}    
                        {isBelowOptimizeMinimum && (
                            <div className="text-xxs text-center text-orange-600 font-medium">Min. lista $20.000</div>
                        )}
                        {/* <div className="text-xxs text-center flex flex-row items-center gap-2 "><Info className="w-4 h-4"/>Sujeto a mínimos de compra y costos de envío por tienda</div>                                                 */}
                    </div>     
                </SheetFooter>                
            </SheetContent>
            <LocationRequiredDialog
                open={isLocationDialogOpen}
                onOpenChange={handleLocationDialogOpenChange}
                onLocationSelected={handleLocationSelected}
            />
        </Sheet>
    );
}



interface CartContentProps extends React.HTMLAttributes<HTMLDivElement> {
    cart: CartItem[];
    storeList:StoreData[],
    addDeletefromCart:(payload:Payload)=>void,
    removeFromCart:(payload: Payload)=>void,
    clearCart:()=>void,
    isLogged:boolean,
    /** Number of linked item insertions currently in flight — renders N skeleton placeholders. */
    insertingCount?: number;
    setOpen?: (open: boolean) => void,
}

const CartContent = forwardRef<HTMLDivElement, CartContentProps>(({cart, storeList, addDeletefromCart, removeFromCart, clearCart, isLogged, insertingCount = 0, setOpen, ...props}, ref) => {
    const pendingItems = cart.filter((i) => i.is_pending_resolution);
    const normalCart = cart.filter((i) => !i.is_pending_resolution);
    const cartGroups = groupCartItemsByCategory(normalCart);

    return (
    <ScrollArea type="scroll" className="h-full w-full flex-1 overflow-hidden">                                                        
        {
        cart.length === 0 ? (
        <>
        <Empty className="my-20">
            <EmptyHeader>
                <EmptyMedia variant="icon">
                <ShoppingCart />
                </EmptyMedia>
                <EmptyTitle>
                    Tu Carrito está vacío
                </EmptyTitle>
                <EmptyDescription>
                    {isLogged ? "Aún no has agregado productos. Empieza tu compra y te mostraremos cómo optimizar precios entre tiendas." : "Inicia sesión para comenzar a agregar productos a tu carrito y optimizar tus compras."}
                </EmptyDescription>
                {!isLogged &&(
                <EmptyContent>
                    <LoginDialog/>
                </EmptyContent>
                )}
            </EmptyHeader>
        </Empty>                                
        </>
        ):(
        <>
        {/* ── Pending resolution section (unresolved items) ───── */}
        {pendingItems.length > 0 && (
            <div className="mb-4">
                <div className="mb-2 flex items-center gap-2 rounded-md bg-primary/10 px-3 py-2 text-xs font-semibold text-primary">
                    <WandSparkles className="h-4 w-4" />
                    <span>Productospendientes...</span>
                </div>
                <div className="space-y-2">
                    {pendingItems.map((item) => (
                        <div key={item.id} className="rounded-lg border bg-card p-3 space-y-2">
                            <Skeleton className="h-4 w-3/4" />
                            <Skeleton className="h-3 w-1/2" />
                        </div>
                    ))}
                </div>
            </div>
)}
        {/* ── Regular cart items ────────────────────────────────────── */}
        {cartGroups.map((group) => {
            return (
                <div key={group.category.id} className="mt-4 first:mt-0 w-full overflow-hidden">
                    <div className="mb-2 flex items-center gap-2 rounded-md bg-white/80 px-3 py-2 text-xs font-semibold text-foreground shadow-sm ring-1 ring-border/60">
                        <MenuIcon iconName={group.category.icon} size={16} className="text-primary"/>
                        <span>{group.category.name}</span>
                    </div>
                    <div className="space-y-1 w-full overflow-hidden">
                        {group.items.map((item) => {
                            return (
                                <CartElement key={item.id} item={item} cart={cart} storeList={storeList} addDeletefromCart={addDeletefromCart} removeFromCart={removeFromCart} setOpen={setOpen}/>
                            );
                        })}
                    </div>
                </div>
            );
        })}
            {/* ── In-flight insertion skeletons ─────────────────────────── */}
            {insertingCount > 0 && (
                <div className="mt-4 space-y-1" data-testid="inserting-skeletons">
                    {Array.from({ length: insertingCount }).map((_, i) => (
                        <div key={`inserting-skeleton-${i}`} className="rounded-lg border bg-card p-3 space-y-2">
                            <Skeleton className="h-4 w-3/4" />
                            <Skeleton className="h-3 w-1/2" />
                        </div>
                    ))}
                </div>
            )}
            <div className="w-full flex justify-center">
                <AlertDialog>
                <AlertDialogTrigger asChild>
                    <Button size="sm" variant="destructive" className="mt-6">
                        <Trash className=""/>Vaciar Carrito
                    </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                    <AlertDialogHeader>
                    <AlertDialogTitle>¿Quieres descartar todos los productos del carrito?</AlertDialogTitle>
                    <AlertDialogDescription>

                    </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction  onClick={() => {clearCart()}}>Vaciar</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
                </AlertDialog>
            </div>
        </>
        )
        }
    </ScrollArea>
    );
});

interface CartCardContentProps extends React.HTMLAttributes<HTMLDivElement> {
    cart: CartItem[];
    item:CartItem,
    storeList:StoreData[],
    addDeletefromCart:(payload:Payload)=>void,
    removeFromCart:(payload: Payload)=>void,
    setOpen?: (open: boolean) => void,
}

const CartElement = forwardRef<HTMLDivElement,CartCardContentProps>(({item,...props}, ref) => {
    const [openPreferences, setOpenPreferences] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const { setProductDecision } = useCart();
    const baseProduct = item.price_component[0];
    const price = item.price_component.reduce((acc, pc) => {
        return acc + (pc.price);
    }, 0);
    const categoryId = getCartItemCategoryId(item);
    const qty = get_total_qty_per_prod(item.id,props.cart);
    if (!baseProduct) {
        // eslint-disable-next-line no-console
        console.warn("[CartElement] item without price_component, skipping render", item);
        return null;
    }
    const imageUrl = baseProduct.images?.thumbs ?? baseProduct.image_url;
    return (
    <Card  className="text-sm w-full p-3 mt-1 overflow-hidden">
        <CardContent className="flex flex-col gap-2 p-0 text-xs py-1 items-start">
            {
            item.is_ai_resolved && (
                <div className="max-w-full overflow-hidden">
                    <span className="bg-orange-50 text-xxs text-muted-foreground font-medium inline-grid grid-cols-[auto_auto_minmax(0,1fr)] items-center gap-1 overflow-hidden rounded-sm px-2 py-2 max-w-full">
                        <Sparkles className="shrink-0 size-3" />

                        <span className="whitespace-nowrap">
                        Mejor match para:
                        </span>

                        <span className="font-semibold truncate min-w-0">
                        "{item.raw_description}"
                        </span>
                    </span>
                </div>
                )
            }
            <div className={cn("flex gap-3 text-xs min-w-0 overflow-hidden w-full",
                                {"border-l-2 border-primary/50":item.is_ai_resolved})}>
                <div className="shrink-0">
                    <Link to={"/"+item.slug} onClick={() => props.setOpen?.(false)}>
                        <img className="Image-60" src={imageUrl} alt={""}/>
                    </Link>
                </div>
                <div className="flex flex-col items-start min-h-20 w-full gap-1 min-w-0">
                    <div className="flex flex-row gap-2 items-start justify-between w-full min-w-0 overflow-hidden">
                        <Link to={"/"+item.slug} className="min-w-0 overflow-hidden" onClick={() => props.setOpen?.(false)}>
                            <p className="font-semibold min-w-0 overflow-hidden">{baseProduct.name}</p></Link>
                    </div>                
                    <p className="text-muted-foreground font-semibold">
                        <Amount 
                            amount={price/qty} 
                            currency="CLP" 
                            locale="es-CL" 
                            />
                    </p>
                    <div className="flex flex-row justify-between items-center w-full mt-1 gap-1">
                        <AddDeleteCartButtons 
                            className=""
                            product_id={item.id}
                            category_id={categoryId}
                            sku={item.price_component[0]?.sku}
                            name={item.price_component[0]?.name}
                            quantity={qty}
                            displayQuantity={qty}
                            showDelete={false}
                            method={item.method}
                            PlusMinusCallback={props.addDeletefromCart}
                            RemoveCallBack={props.removeFromCart}                                
                        />
                        <span className="font-bold text-sm">
                            <Amount 
                            amount={price} 
                            currency="CLP" 
                            locale="es-CL" 
                            />
                        </span>
                    </div>
                    <div className="flex gap-2 items-center text-muted-foreground mt-3 -mx-2">
                        {item.is_ai_resolved ? (
                            <>
                                <Button variant="ghost" size={"xs"} onClick={() => setPickerOpen(true)}>
                                    <Search className="h-4 w-4 shrink-0 text-muted-foreground cursor-pointer"/>
                                    <span className="font-semibold underline underline-offset-4">¿no es lo que buscas?</span>
                                </Button>
                                {pickerOpen && (
                                    <ProductSearchPicker
                                        open={pickerOpen}
                                        onOpenChange={setPickerOpen}
                                        initialQuery={item.raw_description || baseProduct.name}
                                        onProductSelected={(payload) => {
                                            props.removeFromCart({ id: item.id, category_id: categoryId, sku: baseProduct.sku, quantity: 0 });
                                            props.addDeletefromCart({ ...payload, quantity: qty });
                                        }}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <Button variant="ghost" size={"xs"} onClick={() => setOpenPreferences(true)}>
                                    <ArrowLeftRight className="h-4 w-4 shrink-0 text-muted-foreground cursor-pointer"/>
                                    <span className="font-semibold">
                                        <span className="underline underline-offset-4">¿Si no hay stock?</span>
                                        {item.productDecision ? 
                                            (<span className="no-underline text-foreground">: {REPLACEMENT_ACTION_LABELS[item.productDecision.action]}</span>) 
                                            : <span className="no-underline text-muted-foreground opacity-60 italic">: Mejor alternativa</span>}
                                    </span>
                                </Button>
                                {openPreferences && (
                                    <ReplacementPreferences 
                                        product={item} open={openPreferences} onOpenChange={setOpenPreferences}
                                        onDecisionConfirmed={(decision) => setProductDecision(item.id, decision)}
                                    />
                                )}
                            </>
                        )}
                    </div> 
                </div>   
            </div>
        </CardContent>
    </Card>
    );
});
